import React, { useContext, useRef, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { InitialLanesData, MainStore, Lane } from "../../store";
import { UPLOAD_LANE, UploadLaneAction } from "../../actions/actionTypes";
import AntModal from "../../../components/AntModal";
import { Container } from "./styles";
import { Button } from "../Button";
import { LaneName } from "../LaneName";
import { laneColors } from "../../util/lane_color_helpers";
import { Alert } from "antd";
import { AuthContext } from "../../../context/AuthProvider";
import gql from "graphql-tag";
import { useLazyQuery } from "react-apollo";
import { ExternalTrackRow, MatchingTrackData } from "../ExternalTrackRow";

const TRACKS_PER_PAGE = 8;

const GET_MATCHING_RHYTHMIC_TRACKS = gql`
  query MatchingRhythmicTracks(
    $bpm: Float!
    $excludeTrackId: ID!
    $limit: Int
  ) {
    matchingRhythmicTracks(
      bpm: $bpm
      excludeTrackId: $excludeTrackId
      limit: $limit
    ) {
      trackId
      trackTitle
      initialBeat
      trackDuration
      stems {
        stemName
        stemMp3File
        stemWavFile
        instrumentName
        instrumentCategory
        regions {
          visualStartTime
          visualDuration
        }
      }
    }
  }
`;

const GET_MATCHING_HARMONIC_TRACKS = gql`
  query MatchingHarmonicTracks(
    $bpm: Float!
    $key: String!
    $excludeTrackId: ID!
    $limit: Int
  ) {
    matchingHarmonicTracks(
      bpm: $bpm
      key: $key
      excludeTrackId: $excludeTrackId
      limit: $limit
    ) {
      trackId
      trackTitle
      initialBeat
      trackDuration
      stems {
        stemName
        stemMp3File
        stemWavFile
        instrumentName
        instrumentCategory
        regions {
          visualStartTime
          visualDuration
        }
      }
    }
  }
`;

export const AddLaneModal = ({ modalState }) => {
  const [dragActive, setDragActive] = React.useState(false);
  const [uploadError, setUploadError] = React.useState("");
  const [tracksLimit, setTracksLimit] = useState(TRACKS_PER_PAGE);
  const [matchingTracks, setMatchingTracks] = useState<MatchingTrackData[]>([]);
  const [harmonicTracksLimit, setHarmonicTracksLimit] = useState(
    TRACKS_PER_PAGE
  );
  const [matchingHarmonicTracks, setMatchingHarmonicTracks] = useState<
    MatchingTrackData[]
  >([]);
  const [rhythmicExpanded, setRhythmicExpanded] = useState(true);
  const [harmonicExpanded, setHarmonicExpanded] = useState(false);

  const fileInputRef = useRef(null);

  const originalLanes = useSelector<MainStore, InitialLanesData[]>(
    state => state.project.initialTrackData.lanes
  );
  const lanes = useSelector<MainStore, Lane[]>(state =>
    Object.values(state.project.lanes.byId)
  );
  const bpm = useSelector<MainStore, number>(
    state => state.project.initialTrackData.bpm
  );
  const trackKey = useSelector<MainStore, string>(
    state => state.project.initialTrackData.key
  );
  const trackId = useSelector<MainStore, string>(
    state => state.project.initialTrackData.id
  );
  const originalLaneColors = originalLanes.map(origLane => {
    const correspondingLane = lanes.filter(lane => {
      return lane.originalId === origLane.id;
    });
    return correspondingLane.length > 0
      ? correspondingLane[0].color
      : laneColors[laneColors.length - 1][0];
  });
  const dispatch = useDispatch();

  const user = useContext(AuthContext);

  // Query for matching BPM rhythmic tracks from other tracks
  const [
    fetchMatchingTracks,
    { data: matchingTracksData, loading: loadingTracks }
  ] = useLazyQuery(GET_MATCHING_RHYTHMIC_TRACKS, {
    fetchPolicy: "network-only"
  });

  // Query for matching BPM + key harmonic tracks from other tracks
  const [
    fetchHarmonicTracks,
    { data: harmonicTracksData, loading: loadingHarmonicTracks }
  ] = useLazyQuery(GET_MATCHING_HARMONIC_TRACKS, {
    fetchPolicy: "network-only"
  });

  // Reset limit and fetch matching tracks when modal opens
  useEffect(() => {
    if (modalState.visible && bpm && bpm > 1 && trackId) {
      setTracksLimit(TRACKS_PER_PAGE);
      setMatchingTracks([]);
      fetchMatchingTracks({
        variables: {
          bpm,
          excludeTrackId: trackId,
          limit: TRACKS_PER_PAGE
        }
      });

      // Fetch harmonic tracks if we have a key
      if (trackKey) {
        setHarmonicTracksLimit(TRACKS_PER_PAGE);
        setMatchingHarmonicTracks([]);
        fetchHarmonicTracks({
          variables: {
            bpm,
            key: trackKey,
            excludeTrackId: trackId,
            limit: TRACKS_PER_PAGE
          }
        });
      }
    }
  }, [
    modalState.visible,
    bpm,
    trackKey,
    trackId,
    fetchMatchingTracks,
    fetchHarmonicTracks
  ]);

  // Refetch rhythmic tracks when limit increases
  useEffect(() => {
    if (
      modalState.visible &&
      bpm &&
      bpm > 1 &&
      trackId &&
      tracksLimit > TRACKS_PER_PAGE
    ) {
      fetchMatchingTracks({
        variables: {
          bpm,
          excludeTrackId: trackId,
          limit: tracksLimit
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tracksLimit]);

  // Refetch harmonic tracks when limit increases
  useEffect(() => {
    if (
      modalState.visible &&
      bpm &&
      bpm > 1 &&
      trackKey &&
      trackId &&
      harmonicTracksLimit > TRACKS_PER_PAGE
    ) {
      fetchHarmonicTracks({
        variables: {
          bpm,
          key: trackKey,
          excludeTrackId: trackId,
          limit: harmonicTracksLimit
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [harmonicTracksLimit]);

  // Update local state when rhythmic tracks query returns data
  useEffect(() => {
    if (matchingTracksData?.matchingRhythmicTracks) {
      setMatchingTracks(matchingTracksData.matchingRhythmicTracks);
    }
  }, [matchingTracksData]);

  // Update local state when harmonic tracks query returns data
  useEffect(() => {
    if (harmonicTracksData?.matchingHarmonicTracks) {
      setMatchingHarmonicTracks(harmonicTracksData.matchingHarmonicTracks);
    }
  }, [harmonicTracksData]);

  const hasMoreTracks = matchingTracks.length === tracksLimit;
  const isInitialLoad = loadingTracks && matchingTracks.length === 0;
  const isLoadingMore = loadingTracks && matchingTracks.length > 0;

  const hasMoreHarmonicTracks =
    matchingHarmonicTracks.length === harmonicTracksLimit;
  const isInitialHarmonicLoad =
    loadingHarmonicTracks && matchingHarmonicTracks.length === 0;
  const isLoadingMoreHarmonic =
    loadingHarmonicTracks && matchingHarmonicTracks.length > 0;

  const loadMoreTracks = () => {
    setTracksLimit(prev => prev + TRACKS_PER_PAGE);
  };

  const loadMoreHarmonicTracks = () => {
    setHarmonicTracksLimit(prev => prev + TRACKS_PER_PAGE);
  };

  const openFileSelect = () => {
    setUploadError("");
    fileInputRef.current.click();
  };

  const handleDrag = function(e) {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = event => {
    event.preventDefault();
    event.stopPropagation();
    setUploadError("");
    setDragActive(false);
    uploadAudio(event);
  };

  const uploadAudio = event => {
    const fileList: FileList = event.target.files || event.dataTransfer.files;
    if (fileList.length === 0) {
      return;
    }

    const file = fileList.item(0);

    try {
      const isWavFile = file.name.toLowerCase().endsWith(".wav");
      const isMp3File = file.name.toLowerCase().endsWith(".mp3");

      if (user.user.variationEditor) {
        if (!isWavFile) {
          throw Error(
            "Invalid audio file. Only .wav uploads are allowed for content creators."
          );
        }
      } else {
        if (!isWavFile && !isMp3File) {
          throw Error(
            "Invalid audio file. Only .wav or .mp3 files are allowed."
          );
        }
      }

      const url = URL.createObjectURL(file);
      const fileName = file.name.replace(".wav", "").replace(".mp3", "");
      dispatch<UploadLaneAction>({
        type: UPLOAD_LANE,
        url,
        name: fileName
      });
      modalState.close();
    } catch (error) {
      setUploadError(
        error.message ||
          "Error loading audio file. Please check that it is a valid .wav or .mp3 file"
      );
    }
  };

  return (
    <AntModal modalState={modalState}>
      <Container>
        {uploadError !== "" && (
          <Alert message={uploadError} type="error" showIcon />
        )}
        <div id="lane-upload-container">
          <div className="two-columns">
            <div
              id="upload-drag-drop-container"
              className={dragActive ? "drag-active" : ""}
              onDragEnter={handleDrag}
            >
              <div className="drag-drop-content">
                <p>Drag and drop audio file here</p>
                <span className="drag-drop-or">or</span>
                <Button height="auto" px="24" onClick={openFileSelect}>
                  Upload Audio File
                </Button>
              </div>
              <input
                id="files"
                type="file"
                accept=".mp3,.wav"
                ref={fileInputRef}
                onChange={uploadAudio}
                hidden
              />
              <div
                id="drag-overlay"
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              ></div>
            </div>
            <div id="original-lanes-container">
              <div id="original-lanes-header">Original Tracks</div>
              <div id="original-lanes-list-container">
                {originalLanes.map((lane, iLane) => (
                  <LaneName
                    key={lane.id}
                    lane={lane}
                    color={originalLaneColors[iLane]}
                    afterAdd={modalState.close}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
        {bpm > 1 && (
          <div id="matching-stems-container">
            <div
              id="matching-stems-header"
              className="collapsible-header"
              onClick={() => setRhythmicExpanded(!rhythmicExpanded)}
            >
              <svg
                className={`caret-icon ${rhythmicExpanded ? "expanded" : ""}`}
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
              >
                <path
                  d="M4 2L8 6L4 10"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Matching Rhythmic Stems{" "}
              <span className="header-params">({bpm} BPM)</span>
            </div>
            {rhythmicExpanded && (
              <>
                <div
                  id="matching-stems-list-container"
                  className={
                    matchingTracks.length > TRACKS_PER_PAGE ? "scrollable" : ""
                  }
                >
                  {isInitialLoad ? (
                    <div className="loading-text">
                      Loading matching tracks...
                    </div>
                  ) : matchingTracks.length > 0 ? (
                    matchingTracks.map((track, index) => (
                      <ExternalTrackRow
                        key={`${track.trackId}-${index}`}
                        track={track}
                        trackIndex={index}
                        afterAdd={modalState.close}
                      />
                    ))
                  ) : (
                    <div className="no-matches-text">
                      No matching rhythmic stems found at {bpm} BPM
                    </div>
                  )}
                </div>
                {hasMoreTracks && (
                  <button
                    className="load-more-btn"
                    onClick={loadMoreTracks}
                    disabled={isLoadingMore}
                  >
                    {isLoadingMore ? "Loading..." : "Load More"}
                  </button>
                )}
              </>
            )}
          </div>
        )}
        {bpm > 1 && trackKey && (
          <div id="matching-stems-container">
            <div
              id="matching-stems-header"
              className="collapsible-header"
              onClick={() => setHarmonicExpanded(!harmonicExpanded)}
            >
              <svg
                className={`caret-icon ${harmonicExpanded ? "expanded" : ""}`}
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
              >
                <path
                  d="M4 2L8 6L4 10"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Matching Harmonic Stems{" "}
              <span className="header-params">
                ({bpm} BPM, {trackKey})
              </span>
            </div>
            {harmonicExpanded && (
              <>
                <div
                  id="matching-stems-list-container"
                  className={
                    matchingHarmonicTracks.length > TRACKS_PER_PAGE
                      ? "scrollable"
                      : ""
                  }
                >
                  {isInitialHarmonicLoad ? (
                    <div className="loading-text">
                      Loading matching tracks...
                    </div>
                  ) : matchingHarmonicTracks.length > 0 ? (
                    matchingHarmonicTracks.map((track, index) => (
                      <ExternalTrackRow
                        key={`harmonic-${track.trackId}-${index}`}
                        track={track}
                        trackIndex={index}
                        afterAdd={modalState.close}
                      />
                    ))
                  ) : (
                    <div className="no-matches-text">
                      No matching harmonic stems found at {bpm} BPM in{" "}
                      {trackKey}
                    </div>
                  )}
                </div>
                {hasMoreHarmonicTracks && (
                  <button
                    className="load-more-btn"
                    onClick={loadMoreHarmonicTracks}
                    disabled={isLoadingMoreHarmonic}
                  >
                    {isLoadingMoreHarmonic ? "Loading..." : "Load More"}
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </Container>
    </AntModal>
  );
};
